import * as ActionTypes from './actionTypes';
import { ErrorCode } from './reducers/error';

export interface UpdateApplicationAction {
  type: typeof ActionTypes.UPDATE_APP
  section: string
  property: string
  /** Values can be a boolean, array, string, object, or anything else. This can
   * make type-checking and enforcement a bit more challenging when dealing with
   * unknown variables (overall, not just this.) */
  values: unknown
  subtype: string
}

/**
 * This will update the application domain.
 *
 * Due to the way 'values' is handled, there is a possibility that Redux may not
 * detect a change in state.
 */
export const updateApplication = (
  section: string,
  property: string,
  values: unknown,
  type = ActionTypes.UPDATE_APP
): UpdateApplicationAction => ({
  type: ActionTypes.UPDATE_APP,
  subtype: type,
  section,
  property,
  values,
});

export interface ClearErrorsAction {
  type: typeof ActionTypes.ERROR_CLEAR
  section: 'Errors'
  property: string
  subsection: any
}
/**
 * Special action that provides a way to flush all errors for a section+subsection upon entry so
 * it may be stored with the re-validated data.
 */
export const clearErrors = (property: string, subsection: any) => ({
  section: 'Errors',
  property,
  subsection,
  type: ActionTypes.ERROR_CLEAR,
});

export interface ReportErrorsAction {
  type: typeof ActionTypes.ERROR_UPDATE
  section: 'Errors'
  property: string
  values: ErrorCode[]
}
/**
 * This is a generic function to report any errors for a particular
 * section.
 */
export const reportErrors = (
  section: string,
  subsection: any,
  codes: ErrorCode[]
): ReportErrorsAction => {
  // set the section and subsection, in case not otherwise set
  const mappedCodes = codes.map((err) => ({
    ...err,
    code: `${err.section || section}.${err.subsection || subsection}.${err.code}`,
    section: err.section || section,
    subsection: err.subsection || subsection,
  }));

  return {
    section: 'Errors',
    property: section,
    values: mappedCodes,
    type: ActionTypes.ERROR_UPDATE,
  };
};

// This is used for the relationships. It was previously hidden as a 'property'
// that had a unique payload.
export interface ClearSameSpouseAction {
  type: typeof ActionTypes.CLEAR_SAME_SPOUSE
  payload: boolean
}

export const clearSameSpouse = (confirmed: boolean): ClearSameSpouseAction => ({
  type: ActionTypes.CLEAR_SAME_SPOUSE,
  payload: confirmed,
});

export type ApplicationActions =
  | UpdateApplicationAction
  | ReportErrorsAction
  | ClearErrorsAction
  | ClearSameSpouseAction
